import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Alert, Row, Col, Table, Card } from 'react-bootstrap';
import FalconCloseButton from 'components/common/FalconCloseButton';
import SoftBadge from 'components/common/SoftBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faShieldAlt, faArrowRight, faTimes } from '@fortawesome/free-solid-svg-icons';

const parseJsonSafely = (jsonStr) => {
    if (!jsonStr) return null;
    if (typeof jsonStr === 'object') return jsonStr;
    try {
        return JSON.parse(jsonStr);
    } catch {
        return null;
    }
};

const ModalRevisionOrdenPago = ({
    show,
    onHide,
    orden,
    ordenDetalles = [],
    onContinuar
}) => {
    const totalFormateado = parseFloat(orden?.dTotal || 0).toLocaleString('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    const saldoInsolutoFormateado = parseFloat(orden?.dSaldoInsoluto || 0).toLocaleString('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            backdrop="static"
            centered
            aria-labelledby="modal-revision-orden-pago-title"
        >
            <Modal.Header className="bg-light">
                <div className="d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faShieldAlt} className="text-warning fs-2" />
                    <div>
                        <Modal.Title id="modal-revision-orden-pago-title" className="fs-0 mb-0">
                            Revisión de Orden antes de Registrar Pago
                        </Modal.Title>
                        <span className="fs--2 text-muted">Orden #{orden?.idOrden}</span>
                    </div>
                </div>
                <FalconCloseButton className="btn btn-circle btn-sm transition-base p-0" onClick={onHide} />
            </Modal.Header>

            <Modal.Body className="p-4">
                {/* Alerta de ineditabilidad */}
                <Alert variant="warning" className="border-2 border-warning d-flex align-items-start gap-3 p-3 mb-3">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning fs-2 mt-1 flex-shrink-0" />
                    <div className="fs--1">
                        <div className="fw-bold fs-0 text-warning-emphasis mb-1">
                            ¡Atención! La orden será ineditable tras registrar el pago
                        </div>
                        <p className="mb-1 text-800">
                            Una vez registrado el pago, la orden quedará <strong>bloqueada para edición</strong> y las acciones derivadas (asignación de timbres fiscales, activación de licencias y módulos) <strong>pueden ser irreversibles</strong>.
                        </p>
                        <span className="text-dark fw-semi-bold">
                            Por favor revisa cuidadosamente que los datos fiscales, productos, montos y asignaciones sean correctos antes de continuar.
                        </span>
                    </div>
                </Alert>

                {/* Resumen de cabecera */}
                <Card className="mb-3 border shadow-none bg-light">
                    <Card.Body className="p-3">
                        <h6 className="text-uppercase fs--2 text-muted fw-bold mb-2">Datos Generales de la Orden</h6>
                        <Row className="g-2 fs--1">
                            <Col md={6}>
                                <div><strong className="text-700">Cliente:</strong> {orden?.vNombreCliente || orden?.vCustomerName || orden?.vRazonSocial || 'Sin especificar'}</div>
                                <div><strong className="text-700">RFC:</strong> <span className="font-monospace">{orden?.vRfc || 'Sin RFC'}</span></div>
                                <div><strong className="text-700">Razón Social:</strong> {orden?.vRazonSocial || 'Sin especificar'}</div>
                            </Col>
                            <Col md={6}>
                                <div><strong className="text-700">Vendedor:</strong> {orden?.vVendedor || orden?.vCreatedUser || 'Sin asignar'}</div>
                                <div><strong className="text-700">Forma de Pago:</strong> {orden?.vDescripcionFormaPago || orden?.vClave || 'Por Definir'}</div>
                                <div>
                                    <strong className="text-700">Total:</strong> ${totalFormateado} {orden?.vMoneda}
                                    {Number(orden?.dTipoCambio) > 1 && (
                                        <span className="text-muted ms-1">(T.C. ${Number(orden.dTipoCambio).toFixed(4)})</span>
                                    )}
                                </div>
                                <div>
                                    <strong className="text-700">Saldo por Pagar:</strong>{' '}
                                    <span className="text-danger fw-bold">${saldoInsolutoFormateado} {orden?.vMoneda}</span>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Lista de productos */}
                <h6 className="text-uppercase fs--2 text-muted fw-bold mb-2">
                    Productos en la Orden ({ordenDetalles.length})
                </h6>
                <div className="table-responsive border rounded mb-0" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                    <Table size="sm" hover className="fs--1 mb-0 align-middle">
                        <thead className="bg-200 text-800">
                            <tr>
                                <th className="ps-3 py-2">Producto</th>
                                <th className="py-2 text-center" style={{ width: '80px' }}>Cant.</th>
                                <th className="py-2 text-end" style={{ width: '120px' }}>Total</th>
                                <th className="py-2 pe-3" style={{ width: '220px' }}>Asignación / Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ordenDetalles && ordenDetalles.length > 0 ? (
                                ordenDetalles.map((item, idx) => {
                                    const esTimbres = item.productoTimbres === 1 || item.vTipoProducto === 'PlanFacturacionMx';
                                    const planFact = parseJsonSafely(item.vJsonPlanFacturacion);
                                    const rfcAsociado = Array.isArray(planFact) && planFact.length > 0 ? planFact[0]?.vRfc : null;

                                    const licencias = parseJsonSafely(item.vJsonLicencias);
                                    const idLic = Array.isArray(licencias) && licencias.length > 0 ? licencias[0]?.idLicencia : null;

                                    const licMod = parseJsonSafely(item.vJsonLicenciasModulos);
                                    const idLicMod = Array.isArray(licMod) && licMod.length > 0 ? licMod[0]?.idLicencia : null;

                                    const usuarios = parseJsonSafely(item.vJsonUsuarios);
                                    const idUsr = Array.isArray(usuarios) && usuarios.length > 0 ? usuarios[0]?.idUsuario : null;

                                    const itemTotal = parseFloat(item.dTotal || 0).toLocaleString('es-MX', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    });

                                    return (
                                        <tr key={idx}>
                                            <td className="ps-3 fw-semi-bold text-900">
                                                {item.vNombreProducto}
                                            </td>
                                            <td className="text-center">{item.dCantidad}</td>
                                            <td className="text-end font-monospace">${itemTotal}</td>
                                            <td className="pe-3">
                                                {esTimbres && (
                                                    rfcAsociado ? (
                                                        <SoftBadge bg="success">
                                                            Plan asociado: {rfcAsociado}
                                                        </SoftBadge>
                                                    ) : (
                                                        <SoftBadge bg="warning">
                                                            ⚠️ Sin plan asociado
                                                        </SoftBadge>
                                                    )
                                                )}
                                                {idLic && (
                                                    <SoftBadge bg="info" className="me-1">
                                                        Licencia #{idLic}
                                                    </SoftBadge>
                                                )}
                                                {idLicMod && (
                                                    <SoftBadge bg="primary" className="me-1">
                                                        Módulo Lic. #{idLicMod}
                                                    </SoftBadge>
                                                )}
                                                {idUsr && (
                                                    <SoftBadge bg="secondary" className="me-1">
                                                        Usuario #{idUsr}
                                                    </SoftBadge>
                                                )}
                                                {!esTimbres && !idLic && !idLicMod && !idUsr && (
                                                    <span className="text-muted fs--2">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-center text-muted py-3">
                                        No se encontraron productos en la orden.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </Modal.Body>

            <Modal.Footer className="bg-light d-flex justify-content-between">
                <Button variant="outline-secondary" size="sm" onClick={onHide}>
                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                    Cancelar y revisar orden
                </Button>
                <Button variant="success" size="sm" onClick={onContinuar}>
                    <span>Confirmar y continuar al pago</span>
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ModalRevisionOrdenPago.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    orden: PropTypes.object.isRequired,
    ordenDetalles: PropTypes.array,
    onContinuar: PropTypes.func.isRequired
};

export default ModalRevisionOrdenPago;
